import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { useApi } from '#/api/fetch-api';
import { getMarkets, PerpetualMarket } from '#/api/markets';
import { isOptionMarket } from '#/features/options/functions';
import { useFuturesActiveMarketStoreView } from '#/features/perpetuals/active-market-context';

const selectId = (market: PerpetualMarket | MarketView) => market.symbol;

const adapter = createEntityAdapter<PerpetualMarket>({ selectId });
const selectors = adapter.getSelectors();

const INITIAL_STATE: MarketsState = {
  status: 'loading',
  error: '',
  markets: adapter.getInitialState(),
};

export interface MarketsState {
  readonly status: 'loading' | 'error' | 'done';
  readonly error: string;
  readonly markets: EntityState<PerpetualMarket>;
}

export interface MarketsStoreView {
  readonly status: MarketsState['status'];
  readonly error: MarketsState['error'];
  readonly getAllMarkets: () => readonly PerpetualMarket[];
  readonly getMarket: (
    id: PerpetualMarket['symbol'],
  ) => PerpetualMarket | undefined;
}

export interface MarketsView
  extends Omit<MarketsState, 'markets' | 'activeMarket'> {
  readonly markets: Dictionary<MarketView>;
  readonly activeMarket: MarketView | undefined;
}

// WIP: `MarketsStateCtx` to be removed in favor of using the protected `MarketsStoreViewCtx`
export const [usePerpetualMarketsState, MarketsStateCtx] =
  createSafeContext<MarketsState>('MarketsState');

export const [usePerpetualMarketsStoreView, MarketsStoreViewCtx] =
  createSafeContext<MarketsStoreView>('MarketsStoreView');

export const [usePerpetualMarketsView, MarketsViewCtx] =
  createSafeContext<MarketsView>('MarketsView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function PerpetualMarketsStateProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const storeView = useMemo(() => createStoreView(state), [state]);

  useMarketsFetch(setState);

  return (
    <MarketsStateCtx value={state}>
      <MarketsStoreViewCtx value={storeView}>
        {props.children}
      </MarketsStoreViewCtx>
    </MarketsStateCtx>
  );
}

export function PerpetualMarketsViewProvider(props: ProviderProps) {
  const state = usePerpetualMarketsState();
  const { activeMarket } = useFuturesActiveMarketStoreView();
  const activeMarketSymbol = activeMarket?.symbol;

  const view = useMemo(
    () => createView(state, activeMarketSymbol),
    [state, activeMarketSymbol],
  );

  return <MarketsViewCtx value={view}>{props.children}</MarketsViewCtx>;
}

export function createView(
  state: MarketsState,
  activeMarketSymbol: PerpetualMarket['symbol'] | undefined,
): MarketsView {
  const markets = selectors.selectAll(state.markets).map(marketView);
  const marketsDict = Object.fromEntries(markets.map((m) => [selectId(m), m]));
  const activeMarket =
    activeMarketSymbol != null ? marketsDict[activeMarketSymbol] : undefined;

  return {
    ...state,
    markets: marketsDict,
    activeMarket,
  };
}

export interface MarketView {
  readonly symbol: string;
  readonly baseCurrency: string;
  readonly quoteCurrency: string;
}

function marketView(market: PerpetualMarket): MarketView {
  return {
    symbol: market.symbol,
    baseCurrency: market.base_currency,
    quoteCurrency: market.quote_currency,
  };
}

export function createStoreView(state: MarketsState): MarketsStoreView {
  const getAllMarkets = () => selectors.selectAll(state.markets);
  const getMarket = (id: PerpetualMarket['symbol']) => {
    return selectors.selectById(state.markets, id) ?? undefined;
  };

  return {
    status: state.status,
    error: state.error,
    getAllMarkets,
    getMarket,
  };
}

function useMarketsFetch(
  setState: Dispatch<SetStateAction<MarketsState>>,
): void {
  const [, fetchMarkets] = useApi(getMarkets);

  useEffect(() => {
    setState((state) => ({
      ...state,
      status: 'loading',
      error: '',
    }));

    fetchMarkets({}, (resp) => {
      if (!resp.ok) {
        setState((state) => ({
          ...state,
          status: 'error',
          error: resp.message,
        }));
        return;
      }

      const perpetualMarkets = resp.data.results.filter(
        (market) => !isOptionMarket(market),
      );

      setState((state) => {
        const markets = adapter.setAll(state.markets, perpetualMarkets);

        return {
          status: 'done',
          error: '',
          markets,
        };
      });
    });
  }, [fetchMarkets, setState]);
}
